import { CSSInterpolation } from '@emotion/css/create-instance'
import { keyframes } from '@emotion/react'
import React, { type ReactNode, useRef } from 'react'

import { FocusOn } from '../../../components/FocusOn'
import { CssVars } from '../../themes'
import { Layer } from '../Layer'
import { ModalBackdrop } from '../ModalBackdrop'
import { InternalElevate } from './InternalElevate'

const dialogKeyframes = keyframes`
  from {
    opacity: 0;
    transform: translateY(60px);
  }
  to {
    opacity: 1;
    transform: translateY(0px);
  }
`

type ModalRole = 'dialog' | 'alertdialog'
type ModalVariant = 'narrow' | 'medium' | 'wide' | 'xl' | 'xxl'

const MODAL_WIDTHS: Record<ModalVariant, number> = {
  medium: 408,
  narrow: 312,
  wide: 468,
  xl: 828,
  xxl: 1242,
}

type Props = {
  /**
   * Optional function that is called when the user tries to close the modal in any of the following ways:
   * pressing the `Esc` key, or clicking outside the modal.
   */
  onClose?: () => void
  children: ReactNode
  variant: ModalVariant
  role?: ModalRole
  _experimentalIsFocusLockDisabled?: boolean
  'aria-labelledby': string
  overrideModalStyles?: CSSInterpolation
}

// TODO(shanshan), before we align on consistent way to pass the inner children's props, let's use hard-coded string for now.
export const modalBackDropTestId = 'rh-design-modal-backdrop'

/**
 * Renders a modal built according to [WAI-ARIA Authoring Practices 1.1](https://www.w3.org/TR/wai-aria-practices/#dialog_modal).
 */
export function InternalModal({
  _experimentalIsFocusLockDisabled = false,
  'aria-labelledby': ariaLabelledBy,
  children,
  onClose,
  overrideModalStyles,
  role = 'dialog',
  variant,
}: Props): React.ReactElement | null {
  // TODO(yan): we can replace all this code with a simple e.stopPropagation()
  //  once we've made react 17 the minimum version. that wouldn't work with the
  //  event delegation logic of react 16 and below

  const lastDialogClickTimestampRef = useRef<number>(undefined)
  const handleModalClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      lastDialogClickTimestampRef.current = e.timeStamp
    },
    []
  )
  const handleDocumentClick = React.useCallback(
    (e: MouseEvent) => {
      if (isClickingInModalRef.current) {
        isClickingInModalRef.current = false
        return
      }
      if (onClose && e.timeStamp !== lastDialogClickTimestampRef.current) {
        onClose()
      }
    },
    [onClose]
  )

  // NOTE(jare): When the user clicks somewhere inside the dialog, we
  // record the click event's timestamp. If the background sheet receives
  // a click with the same timestamp, we assume it's the click event from
  // the dialog that's now propagated up to the background sheet.
  //
  // Why can't we check if the click event's target is inside the dialog?
  // Let's consider the case where someone renders a React portal inside
  // of the dialog. The portal's children are in the dialog's React tree,
  // so click events will propagate upward to this div. However, the actual
  // DOM tree might look very different - the portal's children could be
  // anywhere in the DOM, inside or outside of the dialog's DOM tree.
  //
  // We only want to close the dialog when a user clicks on an element
  // outside of the dialog's *React* tree.
  const isClickingInModalRef = useRef(false)
  const handleModalMouseDown = React.useCallback(() => {
    isClickingInModalRef.current = true
  }, [])

  // NOTE(omer.demirkan) If you touch this code later,
  // please consider adding isolation with aria-hidden
  // and adding scroll locking.
  return (
    <Layer onDocumentClick={handleDocumentClick} onEscape={onClose}>
      <FocusOn
        autoFocus
        focusLock={!_experimentalIsFocusLockDisabled}
        noIsolation
        returnFocus
      >
        <ModalBackdrop data-testid={modalBackDropTestId}>
          <div
            css={{
              // We're using these :before and :after pseudo-elements to provide visual spacing around the modal. We do it this way instead of via margin so that the elements can shrink down (via flex) when the Modal content has a large height.
              '&:after': {
                content: "''",
                display: 'block',
                height: '20vh',
                // This min-height ensures there is always a small amount of space between the modal and the bottom of the page.
                minHeight: 16,
              },
              '&:before': {
                content: "''",
                display: 'block',
                height: '20vh',
                // This min-height ensures there is always a small amount of space between the modal and the top of the page.
                minHeight: 16,
              },
              alignItems: 'center',
              bottom: '0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              left: 0,
              overflow: 'auto',
              position: 'fixed',
              right: 0,
              top: 0,
            }}
          >
            <InternalElevate>
              {({ rootClassName: internalElevateClassName }) => (
                <section
                  className={internalElevateClassName}
                  css={{
                    animationDuration: '300ms',
                    animationName: dialogKeyframes.toString(),
                    maxWidth: '100%',
                    position: 'relative',
                  }}
                  onClick={handleModalClick}
                  onMouseDown={handleModalMouseDown}
                >
                  <div
                    aria-labelledby={ariaLabelledBy}
                    aria-modal="true"
                    css={[
                      {
                        borderRadius: CssVars.size.borderRadius,
                        boxShadow: `0px 1px 6px ${CssVars.shadow.s1}`,
                        boxSizing: 'border-box',
                        maxWidth: '100%',
                        width: MODAL_WIDTHS[variant],
                      },
                      overrideModalStyles,
                    ]}
                    role={role}
                  >
                    {children}
                  </div>
                </section>
              )}
            </InternalElevate>
          </div>
        </ModalBackdrop>
      </FocusOn>
    </Layer>
  )
}
